// ============================================================
// CODIVUS — Public site entry point + router + toast
//
// This is the public ("outside") marketing site: landing, pricing,
// features, install, docs, blog, demos, enterprise. Every page is
// rendered from ./pages/auth.ts. There is no dashboard, no auth gate,
// no SSO and no API/data loading here — the only "auth" touchpoint is a
// "Sign in" button which auth.ts points at the platform app
// (platform.codivus.africa); the platform owns the entire sign-in flow.
// ============================================================

import * as Auth from './pages/auth.js';
import { showLoader, finishLoader } from './loader.js';

// ---- Router state ----
let currentPath = '/';

// navigate() — matches the signature auth.ts expects. The optional params
// arg is accepted for compatibility with the platform's navigate() even
// though the public router has no use for it.
export function navigate(path: string, _params?: Record<string, unknown>): void {
  currentPath = path;
  history.pushState({ path }, '', path);
  mount();
}

// ---- Toast ----
type ToastKind = 'success' | 'error' | 'info';

export function toast(kind: ToastKind, title: string, msg: string = ''): void {
  const container = document.getElementById('toast-container');
  if (!container) return;
  const icons: Record<ToastKind, string> = {
    success: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--green)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8l4 4L14 4"/></svg>`,
    error:   `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--red)" stroke-width="2" stroke-linecap="round"><path d="M4 4l8 8M12 4l-8 8"/></svg>`,
    info:    `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="6.5"/><path d="M8 7v4M8 5v.01"/></svg>`,
  };
  const el = document.createElement('div');
  el.className = `toast ${kind}`;
  el.innerHTML = `
    <span class="toast-icon">${icons[kind]}</span>
    <div class="toast-msg">
      <div class="toast-title">${title}</div>
      ${msg ? `<div class="toast-sub">${msg}</div>` : ''}
    </div>`;
  container.appendChild(el);
  setTimeout(() => {
    el.style.transition = 'opacity .2s, transform .2s';
    el.style.opacity = '0';
    el.style.transform = 'translateX(24px)';
    setTimeout(() => el.remove(), 220);
  }, 3800);
}

// ---- Public route table ----
// Each entry maps a path to its render/init pair in ./pages/auth.ts.
// Unknown paths fall back to the Landing page.
const ROUTES: Record<string, { render: () => string; init: () => void }> = {
  '/':           { render: Auth.renderLanding,    init: Auth.initLanding },
  '/pricing':    { render: Auth.renderPricing,    init: Auth.initPricing },
  '/features':   { render: Auth.renderFeatures,   init: Auth.initFeatures },
  '/install':    { render: Auth.renderInstall,    init: Auth.initInstall },
  '/docs':       { render: Auth.renderDocs,       init: Auth.initDocs },
  '/blog':       { render: Auth.renderBlog,       init: Auth.initBlog },
  '/demos':      { render: Auth.renderDemos,      init: Auth.initDemos },
  '/enterprise': { render: Auth.renderEnterprise, init: Auth.initEnterprise },
  '/privacy':    { render: Auth.renderPrivacy,    init: Auth.initPrivacy },
  '/terms':      { render: Auth.renderTerms,      init: Auth.initTerms },
  '/contact':    { render: Auth.renderContact,    init: Auth.initContact },
};

// ---- Per-route document metadata ----
// Keeps the tab title and the meta description in sync with the rendered
// page. The static defaults in index.html match the '/' entry; unknown paths
// fall back to it, mirroring the ROUTES fallback in mount().
const ROUTE_META: Record<string, { title: string; description: string }> = {
  '/':           { title: 'Codivus Code — Ship your code faster',   description: 'Codivus Code is a coding agent that works across your codebase — planning, editing, running tools, and opening pull requests. Built for teams that want real engineering output.' },
  '/pricing':    { title: 'Pricing — Codivus Code',                 description: 'Simple credit-based pricing in USD or MWK. Start free, upgrade to Pro or Team, or talk to sales about Enterprise.' },
  '/features':   { title: 'Features — Codivus Code',                description: 'A full engineering loop: reads your codebase, edits across files, runs and fixes tests, opens pull requests, and logs every tool call.' },
  '/install':    { title: 'Install — Codivus Code',                 description: 'Install Codivus Code as a CLI, native desktop app, VS Code extension, or JetBrains plugin. All surfaces sync to the same workspace.' },
  '/docs':       { title: 'Documentation — Codivus Code',           description: 'Guides, references, and integrations for Codivus Code — from installing the CLI to opening pull requests and webhooks.' },
  '/blog':       { title: 'Blog — Codivus Code',                    description: 'Notes from the Codivus Code team — how the agent is built, what we are learning, and how teams ship with it.' },
  '/demos':      { title: 'Demos — Codivus Code',                   description: 'Example projects produced by Codivus Code from a single prompt — landing pages, dashboards, storefronts, and more.' },
  '/enterprise': { title: 'Enterprise — Codivus Code',              description: 'Codivus Code for large teams: VPC deployment, SSO and role controls, bring-your-own-model, and dedicated support.' },
  '/privacy':    { title: 'Privacy Policy — Codivus Code',          description: 'How Codivus Code collects, uses, and protects your data — account email, billing via payment providers, and the code and prompts the agent processes.' },
  '/terms':      { title: 'Terms of Service — Codivus Code',        description: 'The terms that govern your use of Codivus Code — accounts, plans and credits, your content, and acceptable use.' },
  '/contact':    { title: 'Contact — Codivus Code',                 description: 'Get in touch with the Codivus Code team — support, privacy requests, and enterprise sales.' },
};

function applyRouteMeta(path: string): void {
  const meta = ROUTE_META[path] ?? ROUTE_META['/'];
  document.title = meta.title;
  document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description);
}

// ---- Mount ----
function mount(): void {
  const app = document.getElementById('app');
  if (!app) return;

  // Strip any query string so '/docs?x=1' still resolves to '/docs'.
  const q = currentPath.indexOf('?');
  const path = q === -1 ? currentPath : currentPath.slice(0, q);

  const route = ROUTES[path] ?? ROUTES['/'];
  applyRouteMeta(path);
  app.innerHTML = route.render();
  route.init();
}

// ---- Boot ----
function boot(): void {
  showLoader();

  // Persisted theme wins; otherwise follow the OS preference. Mirrors the
  // pre-paint snippet in index.html (and apps/web) so the two never diverge.
  try {
    let theme = localStorage.getItem('codivus-theme');
    if (theme !== 'light' && theme !== 'dark') {
      theme =
        window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches
          ? 'light'
          : 'dark';
    }
    document.documentElement.dataset.theme = theme;
  } catch {
    document.documentElement.dataset.theme = 'dark';
  }

  currentPath = location.pathname || '/';

  window.addEventListener('popstate', (e) => {
    const st = e.state as { path?: string } | null;
    currentPath = st?.path ?? (location.pathname || '/');
    mount();
  });

  mount();

  // Boot is synchronous (no API/data load on the public site), so let the
  // simulated ramp run briefly before finishing — otherwise the bar would
  // snap 0→100 with nothing to show. This makes it a short branded splash.
  window.setTimeout(finishLoader, 600);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
